import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useSettingsStore } from '../lib/stores/settingsStore';
import { useDealStore } from '../lib/stores/dealStore';
import { useOtpStore } from '../lib/stores/otpStore';
import { generateQuotePublicPDF } from '../lib/pdf';
import { parsePhoneWithPrefix } from '../lib/utils/phonePrefix';
import { buildSignedPdfName } from '../lib/utils/signedPdfName';
import { useApp } from '../components/AppProvider';
import { DealContent } from '../components/signature/DealContent';
import { buildSignatureSummary, summaryLine } from '../components/signature/signatureSummary';
import { ContractPreview } from '../components/signature/ContractPreview';
import { SignaturePad } from '../components/signature/SignaturePad';
import { MultiStepSigning } from '../components/signature/MultiStepSigning';
import {
  fetchSigningPlan,
  isMultiStepPlan,
  type SigningPlan,
} from '../components/signature/signingFlowApi';
import {
  LoadingPanel,
  ErrorPanel,
  SuccessPanel,
  OtpPanel,
} from '../components/signature/SignaturePanels';

/**
 * Pagina pubblica firma documento (route /sign/:id). Brand Flusia mobile-first.
 * Auth: signature_token via URL (RLS permette read a anon via request_url).
 * Rendering PDF: delegato a flusia-core /api/documents/quote/public (drop pdf-legacy).
 */
export function SignaturePage() {
  const { id } = useParams<{ id: string }>();
  const { toast } = useApp();

  const [loading, setLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [signedPdfUrl, setSignedPdfUrl] = useState<string | null>(null);

  const [deal, setDeal] = useState<any>(null);
  const [dealServices, setDealServices] = useState<any[]>([]);
  const [signatureRequest, setSignatureRequest] = useState<any>(null);

  const [needsOtp, setNeedsOtp] = useState(false);
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otpVerified, setOtpVerified] = useState(false);
  const [otpLoading, setOtpLoading] = useState(false);

  // Anteprima PDF contratto (non firmato) mostrata in cima alla pagina
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);

  const [showSignaturePad, setShowSignaturePad] = useState(false);
  const [signatureData, setSignatureData] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signerName, setSignerName] = useState('');

  // Recapiti dove inviare PDF firmato: precompilati da signature_request o client,
  // modificabili dal firmatario prima di confermare.
  const [notifyEmail, setNotifyEmail] = useState('');
  const [notifyPhonePrefix, setNotifyPhonePrefix] = useState('+39');
  const [notifyPhoneNumber, setNotifyPhoneNumber] = useState('');

  // Firma a più passi (contratto, clausole vessatorie, privacy...) e OTP dal core:
  // attiva solo se il modello ha più punti di firma o chiede il codice.
  const [signingPlan, setSigningPlan] = useState<SigningPlan | null>(null);
  const multiStep = isMultiStepPlan(signingPlan);
  const [multiPhone, setMultiPhone] = useState('');

  const { settings, fetchSettings } = useSettingsStore();
  // Dati dell'azienda che ha emesso il preventivo (termini, ragione sociale, indirizzo),
  // dalla stessa RPC: il cliente non ha login, quindi useSettingsStore resta vuoto.
  const [company, setCompany] = useState<Record<string, any> | null>(null);
  const { handleDealSigned } = useDealStore();
  const { sendOtp, verifyOtp } = useOtpStore();
  const priceSummary = useMemo(() => buildSignatureSummary(dealServices), [dealServices]);
  const priceLine = summaryLine(priceSummary);

  // Carica signature_request + deal + services + settings
  useEffect(() => {
    if (!id) {
      setError('Link firma non valido');
      setLoading(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);

        // Check Twilio per OTP opt-in (silenzioso se non configurato)
        try {
          const { data: twilioConfig } = await supabase
            .from('api_configurations')
            .select('id')
            .eq('service_name', 'twilio')
            .maybeSingle();
          if (twilioConfig && !cancelled) setNeedsOtp(true);
        } catch {
          /* Twilio non configurato → niente OTP */
        }

        // Accesso via RPC SECURITY DEFINER: la pagina è anon e le tabelle base
        // (signature_requests/deals/clients/deal_services) sono blindate con RLS.
        // La RPC lavora solo sulla riga del token → nessuna enumerazione.
        const { data: rpcData, error: reqError } = await supabase
          .rpc('get_signature_request_by_token', { p_token: id });
        const req = rpcData?.request ?? null;

        if (cancelled) return;
        setCompany(rpcData?.company ?? null);
        if (reqError || !req) {
          setError('Richiesta di firma non trovata o link scaduto');
          return;
        }
        if (req.status === 'completed') {
          setSuccess(true);
          return;
        }
        if (req.status !== 'pending') {
          setError(`Questo link è in stato "${req.status}"`);
          return;
        }
        if (req.expires_at && new Date(req.expires_at) < new Date()) {
          setError('Il link di firma è scaduto');
          return;
        }
        if (!req.deal) {
          setError('Trattativa collegata non trovata');
          return;
        }

        setSignatureRequest(req);
        setDeal(req.deal);
        if (req.deal.client?.business_name) setSignerName(req.deal.client.business_name);
        if (req.deal.client?.phone) setPhone(req.deal.client.phone);

        // Precompila recapiti notifica: prima da signature_request.notification_*
        // (operatore li ha valorizzati dal SendProposalModal), fallback su client.
        const initialEmail = req.notification_email || req.deal.client?.email || '';
        const initialPhoneRaw = req.notification_phone
          || req.deal.client?.whatsapp_number
          || req.deal.client?.phone
          || '';
        setNotifyEmail(initialEmail);
        const parsed = parsePhoneWithPrefix(initialPhoneRaw);
        setNotifyPhonePrefix(parsed.prefix);
        setNotifyPhoneNumber(parsed.number);

        if (!cancelled) setDealServices(rpcData?.deal_services ?? []);
        if (!cancelled) setMultiPhone(initialPhoneRaw || '');

        // Piano di firma dal core. Se non risponde resta la firma singola di sempre.
        try {
          const plan = await fetchSigningPlan(id);
          if (!cancelled) setSigningPlan(plan);
        } catch (planErr) {
          console.warn('Piano di firma non disponibile, firma singola:', planErr);
        }

        await fetchSettings();
      } catch (err) {
        console.error('Error loading signature request:', err);
        if (!cancelled) setError('Impossibile caricare la richiesta di firma');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [id, fetchSettings]);

  // Genera l'anteprima del contratto (PDF non firmato) una volta caricato il deal.
  // Stesso documento che verrà firmato — mostrato in cima alla pagina.
  useEffect(() => {
    if (!id || !deal || success || error) return;
    let objectUrl: string | null = null;
    let cancelled = false;
    setPreviewLoading(true);
    generateQuotePublicPDF({ signatureToken: id, signed: false })
      .then((blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setPreviewPdfUrl(objectUrl);
      })
      .catch((err) => {
        console.warn('Anteprima contratto non disponibile:', err);
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
    // Solo all'arrivo del deal (id stabile)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, deal?.id]);

  const handleSendOtp = async () => {
    if (!phone) {
      toast.error('Inserisci un numero di telefono');
      return;
    }
    setOtpLoading(true);
    try {
      const ok = await sendOtp(phone);
      if (ok) {
        setOtpSent(true);
        toast.success('Codice SMS inviato');
      } else {
        toast.error('Invio codice fallito');
      }
    } catch {
      toast.error('Invio codice fallito');
    } finally {
      setOtpLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    if (otpCode.length < 6) {
      toast.error('Inserisci il codice a 6 cifre');
      return;
    }
    setOtpLoading(true);
    try {
      const ok = await verifyOtp(phone, otpCode);
      if (ok) {
        setOtpVerified(true);
        toast.success('Numero verificato');
      } else {
        toast.error('Codice non valido');
      }
    } catch {
      toast.error('Verifica fallita');
    } finally {
      setOtpLoading(false);
    }
  };

  // Linda Finup 09-06-2026: firma trasparente (PNG) senza sfondo bianco —
  // appare più naturale incollata sulla riga firma del contratto.
  const convertToTransparentPng = (base64Data: string): Promise<string> =>
    new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      img.onload = () => {
        canvas.width = img.width;
        canvas.height = img.height;
        if (!ctx) return reject(new Error('Canvas context not available'));
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = base64Data;
    });

  const base64ToBlob = (base64: string): Blob => {
    const [meta, b64] = base64.split(';base64,');
    const contentType = meta.split(':')[1];
    const raw = window.atob(b64);
    const arr = new Uint8Array(raw.length);
    for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
    return new Blob([arr], { type: contentType });
  };

  const submitSignature = async () => {
    if (!signatureData || !signatureRequest || !deal || !id) return;
    setSubmitLoading(true);
    try {
      // 1) Canvas → PNG trasparente (no sfondo bianco)
      const pngSignatureData = await convertToTransparentPng(signatureData);

      // 2) Upload immagine firma su storage per audit (bucket uploads,
      //    path signatures/% coperto da anon_can_upload_signatures_to_uploads)
      const signatureFileName = `signatures/${deal.id}-${Date.now()}.png`;
      const { error: uploadError } = await supabase.storage
        .from('uploads')
        // upsert:false — l'utente anonimo ha solo la policy INSERT su storage: con
        // upsert (ON CONFLICT DO UPDATE) l'upload viene rifiutato dalla RLS
        // ("new row violates row-level security policy", 02/09/2026). Il nome ha
        // già il timestamp, quindi non ci sono collisioni.
        .upload(signatureFileName, base64ToBlob(pngSignatureData), {
          contentType: 'image/png',
          upsert: false,
        });
      if (uploadError) throw uploadError;
      const {
        data: { publicUrl: signatureUrl },
      } = supabase.storage.from('uploads').getPublicUrl(signatureFileName);

      // 3) Genera PDF firmato via flusia-core (auth: signature_token).
      //    Il backend estrae automaticamente x-forwarded-for per {{CLIENT_IP}}.
      const pdfBlob = await generateQuotePublicPDF({
        signatureToken: id,
        signed: true,
        signatureDataUrl: pngSignatureData,
        signerName: signerName.trim() || undefined,
      });

      await completeSigned(pdfBlob, signatureUrl, null);
    } catch (err) {
      console.error('Error submitting signature:', err);
      toast.error('Impossibile inviare la firma. Riprova.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Firma a più passi: le firme sono già salvate dal core passo per passo,
  // il PDF firmato le prende da lì (nessuna immagine nel body).
  const submitMultiStep = async () => {
    if (!signatureRequest || !deal || !id) return;
    setSubmitLoading(true);
    try {
      const pdfBlob = await generateQuotePublicPDF({
        signatureToken: id,
        signed: true,
        signerName: signerName.trim() || undefined,
      });
      await completeSigned(pdfBlob, null, multiPhone.replace(/\s+/g, ''));
    } catch (err) {
      console.error('Error completing multi-step signature:', err);
      toast.error(err instanceof Error && err.message ? err.message : 'Impossibile completare la firma. Riprova.');
    } finally {
      setSubmitLoading(false);
    }
  };

  // Passi comuni dopo il PDF firmato: upload, recapiti, chiusura offerta, invio.
  const completeSigned = async (
    pdfBlob: Blob,
    signatureUrl: string | null,
    phoneOverride: string | null,
  ) => {
    if (!deal || !id) return;
    {
      // 4) Upload PDF firmato — nome friendly cliente_titolo_quotenumber.pdf
      // (es. TEST_SRL_test_Q-2026-040.pdf). Prefisso deal.id nel path per evitare
      // collisioni tra deal diversi con stesso quote_number cross-org.
      const friendlyName = buildSignedPdfName({
        clientName: deal.client?.business_name,
        dealTitle: deal.title,
        quoteNumber: deal.quote_number,
        dealId: deal.id,
      });
      // Cartella con timestamp: niente upsert (vietato all'anonimo dalla RLS) e
      // nessuna collisione se lo stesso preventivo viene firmato di nuovo.
      const pdfFileName = `${deal.id}/${Date.now()}/${friendlyName}`;
      const { error: pdfError } = await supabase.storage
        .from('signed-pdfs')
        .upload(pdfFileName, pdfBlob, { contentType: 'application/pdf', upsert: false });
      if (pdfError) throw pdfError;
      const {
        data: { publicUrl: pdfUrl },
      } = supabase.storage.from('signed-pdfs').getPublicUrl(pdfFileName);

      // 5) Salva eventuali modifiche ai recapiti notifica fatte dal firmatario
      const composedPhone =
        phoneOverride ??
        (notifyPhoneNumber.trim() ? `${notifyPhonePrefix}${notifyPhoneNumber.replace(/\s+/g, '')}` : '');
      await supabase.rpc('update_signature_notification_by_token', {
        p_token: id,
        p_email: notifyEmail.trim() || null,
        p_phone: composedPhone || null,
      });

      // 6) Finalize via RPC SECURITY DEFINER (bypassa permission denied
      //    user_profiles della catena trigger su deals per role anon).
      const { error: finalizeError } = await supabase.rpc('finalize_signed_deal', {
        p_signature_token: id,
        p_deal_id: deal.id,
        p_signature_url: signatureUrl,
        p_signed_pdf_url: pdfUrl,
        p_signed_at: new Date().toISOString(),
      });
      if (finalizeError) throw finalizeError;

      // 7) Triggera invio PDF firmato al cliente via email/WhatsApp
      //    (best-effort, non blocca UX se fails)
      try {
        const CORE_URL = import.meta.env.VITE_CORE_URL || 'https://core.flusia.it';
        const notifyHeaders: Record<string, string> = { 'Content-Type': 'application/json' };
        const devKey = import.meta.env.VITE_FLUSIA_CORE_DEV_KEY;
        if (devKey) notifyHeaders['X-Flusia-Dev-Key'] = devKey;
        const notifyResp = await fetch(`${CORE_URL}/api/documents/quote/public/notify`, {
          method: 'POST',
          headers: notifyHeaders,
          body: JSON.stringify({ signatureToken: id }),
        });
        if (!notifyResp.ok) {
          console.warn('Notify post-signature non-fatal failure:', notifyResp.status);
        }
      } catch (notifyErr) {
        console.warn('Notify post-signature error (non-fatal):', notifyErr);
      }

      // 8) Crea project se applicabile (workflow downstream — anon non ha
      //    permission, ma è best-effort: il flow funziona anche se fails)
      try {
        if (typeof handleDealSigned === 'function') await handleDealSigned(deal.id);
      } catch (workflowErr) {
        console.error('handleDealSigned failed (non-blocking):', workflowErr);
      }

      setSignedPdfUrl(pdfUrl);
      setSuccess(true);
    }
  };

  // Apre il pad di firma, scrolla fino al form e mette il focus sul campo nome
  // (preventScroll per non interrompere lo smooth scroll in corso).
  const openSignaturePad = () => {
    setShowSignaturePad(true);
    setTimeout(() => {
      document
        .getElementById('signature-pad')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      (document.getElementById('signer-name-input') as HTMLInputElement | null)
        ?.focus({ preventScroll: true });
    }, 100);
  };

  // Render branching
  if (loading) return <LoadingPanel message="Caricamento richiesta di firma…" />;
  if (error) return <ErrorPanel message={error} onClose={() => window.close()} />;
  if (success) return <SuccessPanel signerName={signerName} pdfUrl={signedPdfUrl} />;
  if (needsOtp && !otpVerified && !multiStep) {
    return (
      <OtpPanel
        phone={phone}
        onPhoneChange={setPhone}
        otpCode={otpCode}
        onOtpChange={setOtpCode}
        otpSent={otpSent}
        loading={otpLoading}
        onSendOtp={handleSendOtp}
        onVerifyOtp={handleVerifyOtp}
        onChangeNumber={() => {
          setOtpSent(false);
          setOtpCode('');
        }}
      />
    );
  }

  // Vista principale: revisione documento + firma
  return (
    <div className="min-h-screen pb-32 sm:pb-12" style={{ background: '#FBFBFA' }}>
      {/* Header brand */}
      <header className="sticky top-0 z-20 backdrop-blur-md bg-[#FBFBFA]/85 border-b border-black/[0.06]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <div
            className="text-base sm:text-lg font-semibold bg-clip-text text-transparent"
            style={{ backgroundImage: 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)' }}
          >
            Flusia
          </div>
          {!showSignaturePad && (
            <button
              type="button"
              onClick={openSignaturePad}
              className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold text-white rounded-xl shadow-md hover:shadow-lg transition-all active:scale-[0.98]"
              style={{ background: 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)' }}
            >
              <FileText className="h-4 w-4" />
              Procedi alla firma
            </button>
          )}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-8 space-y-6">
        <DealContent
          deal={deal}
          dealServices={dealServices}
          summary={priceSummary}
          company={company ?? settings}
          expiresAt={signatureRequest?.expires_at}
          notifyEmail={notifyEmail}
          contractSlot={<ContractPreview pdfUrl={previewPdfUrl} loading={previewLoading} />}
        />

        {showSignaturePad && multiStep && id && (
          <div id="signature-pad">
            <MultiStepSigning
              token={id}
              plan={signingPlan}
              signerName={signerName}
              onSignerNameChange={setSignerName}
              notifyEmail={notifyEmail}
              onNotifyEmailChange={setNotifyEmail}
              notifyPhone={multiPhone}
              onNotifyPhoneChange={setMultiPhone}
              completing={submitLoading}
              onComplete={submitMultiStep}
            />
          </div>
        )}

        {showSignaturePad && !multiStep && (
          <div id="signature-pad">
            <SignaturePad
              onSignatureChange={setSignatureData}
              loading={submitLoading}
              onSubmit={submitSignature}
              termsAccepted={termsAccepted}
              onTermsToggle={setTermsAccepted}
              signerName={signerName}
              onSignerNameChange={setSignerName}
              notifyEmail={notifyEmail}
              onNotifyEmailChange={setNotifyEmail}
              notifyPhonePrefix={notifyPhonePrefix}
              onNotifyPhonePrefixChange={setNotifyPhonePrefix}
              notifyPhoneNumber={notifyPhoneNumber}
              onNotifyPhoneNumberChange={setNotifyPhoneNumber}
            />
          </div>
        )}
      </main>

      {/* Sticky CTA mobile (visibile solo se non si è ancora cliccato "Procedi") */}
      {!showSignaturePad && (
        <div
          className="fixed bottom-0 left-0 right-0 sm:hidden z-30 px-4 py-3 backdrop-blur-md border-t border-black/[0.06]"
          style={{ background: 'rgba(251,251,250,0.95)' }}
        >
          {priceLine && (
            <div className="mb-2 flex justify-between text-xs text-[#6B6762]">
              <span>Stai firmando</span>
              <span className="font-semibold text-[#37352F] tabular-nums">{priceLine}</span>
            </div>
          )}
          <button
            type="button"
            onClick={openSignaturePad}
            className="w-full inline-flex justify-center items-center gap-2 px-5 py-3.5 text-base font-semibold text-white rounded-xl shadow-lg active:scale-[0.98] transition-transform"
            style={{ background: 'linear-gradient(135deg, #F57C2A 0%, #D6336C 100%)' }}
          >
            <FileText className="h-5 w-5" />
            Procedi alla firma
          </button>
        </div>
      )}
    </div>
  );
}
